/**
 * Runtime schema introspection.
 *
 * Fetches the schema from the federation endpoint via an introspection query,
 * caches it on disk at ~/.sortlist/schema.json, and returns a GraphQLSchema
 * together with a field → subgraph map derived from the `@join__field`
 * directives the router injects into introspection responses.
 */
import { createHash } from 'node:crypto';
import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import { buildClientSchema, getIntrospectionQuery, type GraphQLSchema, type IntrospectionQuery } from 'graphql';

import { baseRequestHeaders } from '../lib/api-headers.ts';
import { loadApplicationSession } from '../lib/application-session.ts';
import { storedUserAccessToken } from '../lib/auth.ts';
import { clientCredentials, fetchClientCredentialsToken } from '../lib/client-credentials.ts';
import { getEndpoint } from '../lib/config.ts';

const CACHE_DIR = path.join(os.homedir(), '.sortlist');
const CACHE_PATH = path.join(CACHE_DIR, 'schema.json');
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export type FieldServiceMap = Map<string, Map<string, string>>;

export interface IntrospectedSchema {
  schema: GraphQLSchema;
  fieldServices: FieldServiceMap;
  /** All subgraph names observed in the supergraph, regardless of scope visibility. */
  services: Set<string>;
}

let cached: IntrospectedSchema | null = null;

/**
 * The router injects `appliedDirectives` into introspection responses — this
 * isn't a standard introspection field, so we read it from the raw JSON.
 */
interface RawField {
  name: string;
  appliedDirectives?: Array<{
    name: string;
    args: Array<{ name: string; value: unknown }>;
  }>;
}

interface RawType {
  name: string;
  fields?: RawField[];
}

interface RawIntrospection {
  __schema: {
    queryType?: { name: string } | null;
    mutationType?: { name: string } | null;
    types: RawType[];
  };
}

interface CacheEnvelope {
  endpoint: string;
  fetchedAt: number;
  /** Short fingerprint of the access token so schemas for different scopes don't collide. */
  tokenFingerprint: string;
  introspection: IntrospectionQuery;
  raw: RawIntrospection;
}

function tokenFingerprint(token: string | null): string {
  if (!token) return 'anonymous';
  return createHash('sha256').update(token).digest('hex').slice(0, 16);
}

function readCache(
  endpoint: string,
  token: string | null,
): { introspection: IntrospectionQuery; raw: RawIntrospection } | null {
  try {
    const text = fs.readFileSync(CACHE_PATH, 'utf-8');
    const envelope: CacheEnvelope = JSON.parse(text);
    if (envelope.endpoint !== endpoint) return null;
    if (envelope.tokenFingerprint !== tokenFingerprint(token)) return null;
    if (Date.now() - envelope.fetchedAt > CACHE_TTL_MS) return null;
    if (!envelope.raw) return null;
    return { introspection: envelope.introspection, raw: envelope.raw };
  } catch {
    return null;
  }
}

function writeCache(
  endpoint: string,
  token: string | null,
  introspection: IntrospectionQuery,
  raw: RawIntrospection,
): void {
  try {
    if (!fs.existsSync(CACHE_DIR)) {
      fs.mkdirSync(CACHE_DIR, { recursive: true });
    }
    const envelope: CacheEnvelope = {
      endpoint,
      fetchedAt: Date.now(),
      tokenFingerprint: tokenFingerprint(token),
      introspection,
      raw,
    };
    fs.writeFileSync(CACHE_PATH, JSON.stringify(envelope));
  } catch {
    // Non-fatal — worst case we re-fetch next time
  }
}

async function fetchIntrospection(
  endpoint: string,
  token: string | null,
): Promise<{ introspection: IntrospectionQuery; raw: RawIntrospection }> {
  const headers: Record<string, string> = baseRequestHeaders();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query: getIntrospectionQuery() }),
  });

  if (!res.ok) {
    throw new Error(`Introspection failed: HTTP ${res.status} ${res.statusText}`);
  }

  const json = (await res.json()) as {
    data?: IntrospectionQuery & RawIntrospection;
    errors?: Array<{ message: string }>;
  };

  if (json.errors?.length) {
    throw new Error(`Introspection errors: ${json.errors.map((e) => e.message).join(', ')}`);
  }

  if (!json.data) {
    throw new Error('Introspection returned no data');
  }

  return { introspection: json.data as IntrospectionQuery, raw: json.data as RawIntrospection };
}

/**
 * Walk the raw introspection response and build:
 *   - a `typeName → fieldName → subgraph` map from `@join__field(graph: ...)` directives
 *   - the full set of subgraph names observed anywhere in the response (including types
 *     whose root-fields were hidden by scope filtering — still useful so the CLI can
 *     expose empty sub-commands that explain the scope gap)
 */
function parseIntrospection(raw: RawIntrospection): { fieldServices: FieldServiceMap; services: Set<string> } {
  const fieldServices: FieldServiceMap = new Map();
  const services = new Set<string>();

  for (const type of raw.__schema.types ?? []) {
    if (!type.fields?.length) continue;

    let fieldMap: Map<string, string> | null = null;

    for (const field of type.fields) {
      const directive = field.appliedDirectives?.find((d) => d.name === 'join__field');
      if (!directive) continue;
      const graphArg = directive.args.find((a) => a.name === 'graph');
      if (!graphArg || typeof graphArg.value !== 'string') continue;
      services.add(graphArg.value);
      if (!fieldMap) fieldMap = new Map();
      fieldMap.set(field.name, graphArg.value);
    }

    if (fieldMap) fieldServices.set(type.name, fieldMap);
  }

  return { fieldServices, services };
}

/**
 * Fetches and caches the schema via introspection. Returns the built schema
 * along with a map of root-field → subgraph name used for CLI service grouping.
 *
 * @param endpointOverride - Override the configured endpoint URL
 */
async function currentAccessToken(): Promise<string | null> {
  const machine = clientCredentials();
  if (machine) return fetchClientCredentialsToken(machine);

  return loadApplicationSession()?.accessToken ?? storedUserAccessToken();
}

export async function getIntrospectedSchema(endpointOverride?: string): Promise<IntrospectedSchema> {
  if (cached) return cached;

  const endpoint = getEndpoint(endpointOverride);
  const token = await currentAccessToken();

  const fromCache = readCache(endpoint, token);
  if (fromCache) {
    // A cache that no longer builds is discarded rather than raised: one bad
    // fetch — a subgraph restarting mid-introspection is enough — would otherwise
    // fail every command for the whole TTL, and the error blames the query rather
    // than the file.
    const usable = build(fromCache.introspection, fromCache.raw);
    if (usable) {
      cached = usable;
      return cached;
    }
    discardCache();
  }

  const { introspection, raw } = await fetchIntrospection(endpoint, token);
  const built = build(introspection, raw);
  if (!built) {
    throw new Error(
      'The endpoint returned an introspection result this CLI could not build a schema from. ' +
        'Nothing was cached; retry, and check that every subgraph is up.',
    );
  }

  // Written only once the result is known to build, so a partial response cannot
  // become a sticky failure.
  writeCache(endpoint, token, introspection, raw);
  cached = built;
  return cached;
}

function build(introspection: IntrospectionQuery, raw: RawIntrospection): IntrospectedSchema | null {
  try {
    const { fieldServices, services } = parseIntrospection(raw);
    return { schema: buildClientSchema(introspection), fieldServices, services };
  } catch {
    return null;
  }
}

function discardCache(): void {
  try {
    fs.unlinkSync(CACHE_PATH);
  } catch {
    // already gone
  }
}

/**
 * Force-refresh the cached schema, bypassing the disk cache.
 */
export async function refreshSchema(endpointOverride?: string): Promise<IntrospectedSchema> {
  cached = null;
  discardCache();

  const endpoint = getEndpoint(endpointOverride);
  const token = await currentAccessToken();
  const { introspection, raw } = await fetchIntrospection(endpoint, token);
  const built = build(introspection, raw);
  if (!built) {
    throw new Error(
      'The endpoint returned an introspection result this CLI could not build a schema from. ' +
        'Nothing was cached; retry, and check that every subgraph is up.',
    );
  }

  writeCache(endpoint, token, introspection, raw);
  cached = built;
  return cached;
}
